import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import toast from "react-hot-toast";
import { supabase } from "../../lib/supabase";
import { queryKeys } from "../../lib/queryKeys";
import { Task } from "../../types";

interface UpdateTaskOrderVariables {
  taskId: string;
  newOrderPosition: number;
}

interface UpdateTaskOrderContext {
  previousTasks: Task[] | undefined;
}

/**
 * Moves a task to a new position within the board's ordering. Mirrors
 * {@link useUpdateListOrder}, scoped to a task: the cache is re-sorted
 * optimistically so the dropped card settles immediately, and the snapshot is
 * restored if the write fails.
 */
export function useUpdateTaskOrder() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, UpdateTaskOrderVariables, UpdateTaskOrderContext>({
    mutationFn: async ({ taskId, newOrderPosition }) => {
      const { error } = await supabase
        .from("tasks")
        .update({ order_position: newOrderPosition })
        .eq("id", taskId);
      if (error) throw error;
    },

    onMutate: async ({ taskId, newOrderPosition }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.tasks });
      const previousTasks = queryClient.getQueryData<Task[]>(queryKeys.tasks);

      // Left alone when the cache has not loaded: writing `[]` into it would
      // flip useTasks out of its pending state and drop the app's loading gate,
      // and onError's snapshot guard could not put it back.
      queryClient.setQueryData<Task[]>(queryKeys.tasks, (prev) =>
        prev
          ? prev
              .map((t) =>
                t.id === taskId ? { ...t, order_position: newOrderPosition } : t
              )
              .sort((a, b) => a.order_position - b.order_position)
          : prev
      );

      return { previousTasks };
    },

    onError: (err, { taskId }, context) => {
      if (context?.previousTasks) {
        queryClient.setQueryData(queryKeys.tasks, context.previousTasks);
      }
      Sentry.captureException(err, {
        extra: { mutation: "useUpdateTaskOrder", taskId },
      });
      toast.error("Failed to reorder task. Please try again.");
    },

    // Deliberately no onSettled invalidation, unlike useUpdateListOrder. The
    // optimistic re-sort already produces the final state, and queryKeys.tasks
    // refetches the whole task graph (subtasks, tags, attachments, links) —
    // dragging five cards would issue five of them. The implementation this
    // replaces did not invalidate either.
  });
}
